import { Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { NavigationExtras, Router } from '@angular/router';

import { filter, switchMap } from 'rxjs/operators';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { Subscription } from 'rxjs';

import { AuthService } from '../../services/auth/auth.service';
import { ConfirmationModalComponent } from '../../shared/components/confirmation-modal/confirmation-modal.component';
import { ConfirmWindowService } from '../../services/confirm-window/confirm-window.service';
import { environment } from '../../../environments/environment';
import { Permission } from '../../models/permission.model';
import { UserInfoService } from '../../services/user-info/user-info.service';
import AuthRoles from '../../../assets/data/authRoles.json';

@Component({
  selector: 'app-header',
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.scss']
})
export class HeaderComponent implements OnInit, OnDestroy {

  readonly permission = Permission;
  readonly AIDE_HEADER_URL = environment.AIDE_HEADER_URL;
  readonly SANTE_GOUV_URL = environment.SANTE_GOUV_URL;
  readonly E_SANTE_GOUV_URL = environment.E_SANTE_GOUV_URL;
  readonly CREATION_HABILITATION_ROUTES = [
    '/habilitation/cl',
    '/habilitation/gars',
    '/habilitation/gae',
    '/habilitation/mandataire',
    '/habilitation/delegue-structure',
    '/habilitation/delegue',
    '/habilitation/delegue-generique',
    '/habilitation/admin-generique',
    '/habilitation/cl-structure',
  ];
  autorisations: string[];
  roles: string[];
  idMenuOpen = -1;
  utilisateur = '';
  position = ' '; // TODO
  showSubMenu = true;
  showMenuforRoles = false;
  isDelegueAllowed: boolean;
  isCreationMandataire: boolean;
  isVisitor: boolean;
  canHandleCollaborateurs: boolean;

  storedHabilitationsSubscription: Subscription;

  constructor(
    private readonly userInfo: UserInfoService,
    public readonly router: Router,
    private readonly confirmWindowService: ConfirmWindowService,
    private readonly authService: AuthService,
    private readonly modalService: NgbModal
  ) {}

  ngOnInit() {
    this.userInfo.connected.pipe(
      switchMap(() => this.userInfo.canHandleCollaborateurs())
    )
    .subscribe((canHandleCollaborateurs) => {
      this.canHandleCollaborateurs = canHandleCollaborateurs;
      this.roles = this.userInfo.getAuthRoles();
      this.autorisations = this.userInfo.getAuthAutorisations();
      const personne = this.userInfo.getPersonnes();
      this.utilisateur = `${personne?.prenom} ${personne?.nom}`;
      this.isUserRoleAuthorized();
      this.isVisitor = this.roles?.includes('VISITEUR');
    });

    this.storedHabilitationsSubscription = this.userInfo.getStoredHabilitationsUpdatedEvent().pipe(
      filter((isStoredHabilitationsUpdated: boolean) => isStoredHabilitationsUpdated),
      switchMap(() => this.userInfo.canHandleCollaborateurs())
    )
    .subscribe(canHandleCollaborateurs => {
      this.canHandleCollaborateurs = canHandleCollaborateurs;
    });
  }

  ngOnDestroy(): void {
    if (this.storedHabilitationsSubscription && !this.storedHabilitationsSubscription.closed) {
      this.storedHabilitationsSubscription.unsubscribe();
    }
  }

  /**
   * Méthode pour identifier les click en dehors du submenu
   * d'habilitations de l'entête pour la fermeture
   * automatique si c'est le cas.
   */
  @HostListener('document:mousedown', ['$event'])
  documentClick(event: any): void {
    const subMenuOpened = document.getElementById('nav-collapse-submenu--3');
    const subMenuStructuresOpened = document.getElementById('nav-collapse-submenu--2');
    const subMenuLogOut = document.getElementById('collapseUser');

    if (event?.srcElement?.className !== 'nav-link') {
      if (subMenuOpened?.className === 'collapse nav-collapse show') {
        setTimeout(() => this.showSubMenu = !this.showSubMenu, 500);
      }

      if (subMenuStructuresOpened?.className === 'collapse nav-collapse show') {
        setTimeout(() => this.showSubMenu = !this.showSubMenu, 500);
      }

      if (subMenuLogOut?.className === 'collapse nav-collapse nav-collapse--right show') {
        setTimeout(() => this.showSubMenu = !this.showSubMenu, 500);
      }
    }
  }

  checkCanHandleCollaborateurs() {
    this.userInfo.canHandleCollaborateurs().subscribe(canHandle => this.canHandleCollaborateurs = canHandle);
  }

  openMenu(idMenu: number) {
    this.idMenuOpen = this.idMenuOpen === idMenu ? -1 : idMenu;
    if (idMenu === 1 || idMenu === 2 || idMenu === 5) {
      this.showSubMenu = true;
      this.idMenuOpen = idMenu;
    }
  }

  isUserRoleAuthorized() {
    this.showMenuforRoles =
      !!sessionStorage.getItem('personnes') &&
      this.roles?.some((r) => {
        return AuthRoles.includes(r);
      });
  }

  logout() {
    this.userInfo.removeAuth();
    this.userInfo.removeHabilitations();
    this.router.navigate(['home']);
  }

  onBlur() {
    this.idMenuOpen = -1;
  }

  getPermissions(permision: string): boolean {
    return this.userInfo.getPermissions(permision);
  }

  configConfirmWindow() {
    this.confirmWindowService.deconnexion();
  }

  handleConfirmationEvent(isConfirmed: boolean) {
    if (isConfirmed) {
      this.authService.logout();
    }
  }

  navigate(targetRoute: string, params?: any) {
    const navigationExtras: NavigationExtras = {
      queryParams: params,
    };
    const currentUrl = this.router.url.includes('?') ? this.router.url.slice(0, this.router.url.indexOf('?')) : this.router.url;
    const shouldShowModal = this.CREATION_HABILITATION_ROUTES.includes(currentUrl);
    if (shouldShowModal) {
      const modalRef = this.modalService.open(ConfirmationModalComponent);
      this.setModalTexts(modalRef);
      modalRef.result.then((result) => {
        if (!!result) {
          this.router.navigate([targetRoute], navigationExtras);
        }
      });
    } else {
      this.router.navigate([targetRoute], navigationExtras);
    }
  }

  private setModalTexts(modalRef) {
    modalRef.componentInstance.title = 'confirmation-modal.habilitation.cancel.title';
    modalRef.componentInstance.description = 'confirmation-modal.habilitation.cancel.description';
    modalRef.componentInstance.cancelLabel = 'confirmation-modal.habilitation.cancel.cancel';
    modalRef.componentInstance.confirmLabel = 'confirmation-modal.habilitation.cancel.confirm';
  }

}
